import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  Button,
  Switch,
  FormControl,
  FormLabel,
  FormHelperText,
  Divider,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiDownload, FiTrash2, FiZap } from 'react-icons/fi';
import { useDebugStore } from '../../../../stores/debugStore';
import { usePersonalizationData } from '../hooks/usePersonalizationData';
import { useWebSocketDebug } from '../hooks/useWebSocketDebug';

interface DebugControlsProps {
  onClose: () => void;
}

const DebugControls: React.FC<DebugControlsProps> = ({ onClose }) => {
  const {
    autoRefresh,
    showNotifications,
    maxConceptsToShow,
    maxEventsToShow,
    updateSettings,
    exportDebugData,
    resetAllData,
    clearDetectedConcepts,
    clearWsEvents,
  } = useDebugStore();
  
  const { refreshAll, isLoading } = usePersonalizationData();
  const { simulateConceptDetection, simulateSkillUpdate, simulatePromptGeneration } = useWebSocketDebug();
  const toast = useToast();

  const handleResetAll = () => {
    resetAllData();
    toast({
      title: 'Data reset',
      description: 'All debug data has been cleared',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleForceAnalysis = () => {
    // Deprecated: no-op
  };

  return (
    <VStack align="stretch" spacing={6}>
      <Heading size="md">Debug Controls</Heading>

      <Alert status="info" variant="left-accent">
        <AlertIcon />
        <Text fontSize="sm">
          Use keyboard shortcuts: Cmd+Shift+D to toggle panel, Cmd+Shift+E to export data
        </Text>
      </Alert>

      {/* Settings */}
      <VStack align="stretch" spacing={4}>
        <Heading size="sm">Settings</Heading>
        
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="auto-refresh" mb="0">
            Auto Refresh
          </FormLabel>
          <Switch
            id="auto-refresh"
            isChecked={autoRefresh}
            onChange={(e) => updateSettings({ autoRefresh: e.target.checked })}
          />
        </FormControl>

        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="show-notifications" mb="0">
            Show Notifications
          </FormLabel>
          <Switch
            id="show-notifications"
            isChecked={showNotifications}
            onChange={(e) => updateSettings({ showNotifications: e.target.checked })}
          />
        </FormControl>

        <FormControl>
          <FormLabel>Max Concepts to Show</FormLabel>
          <NumberInput
            value={maxConceptsToShow}
            min={10}
            max={200}
            onChange={(_, value) => updateSettings({ maxConceptsToShow: value })}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Number of concepts to keep in log</FormHelperText>
        </FormControl>

        <FormControl>
          <FormLabel>Max Events to Show</FormLabel>
          <NumberInput
            value={maxEventsToShow}
            min={10}
            max={500}
            onChange={(_, value) => updateSettings({ maxEventsToShow: value })}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <FormHelperText>Number of WebSocket events to keep</FormHelperText>
        </FormControl>
      </VStack>

      <Divider />

      {/* Data Actions */}
      <VStack align="stretch" spacing={4}>
        <Heading size="sm">Data Actions</Heading>
        
        <Button
          leftIcon={<FiRefreshCw />}
          onClick={refreshAll}
          isLoading={isLoading}
          loadingText="Refreshing..."
        >
          Refresh All Data
        </Button>

        <Button
          leftIcon={<FiZap />}
          onClick={handleForceAnalysis}
          variant="outline"
          isDisabled
          title="Deprecated"
        >
          Force Profile Analysis (deprecated)
        </Button>

        <Button
          leftIcon={<FiDownload />}
          onClick={exportDebugData}
          variant="outline"
        >
          Export Debug Data
        </Button>

        <HStack>
          <Button
            size="sm"
            onClick={clearDetectedConcepts}
            variant="ghost"
          >
            Clear Concepts
          </Button>
          <Button
            size="sm"
            onClick={clearWsEvents}
            variant="ghost"
          >
            Clear Events
          </Button>
        </HStack>

        <Button
          leftIcon={<FiTrash2 />}
          onClick={handleResetAll}
          colorScheme="red"
          variant="outline"
        >
          Reset All Data
        </Button>
      </VStack>

      <Divider />

      {/* Simulation */}
      <VStack align="stretch" spacing={4}>
        <Heading size="sm">Simulation</Heading>
        <Text fontSize="sm" color="gray.600">
          Generate test events for debugging
        </Text>
        
        <HStack>
          <Button
            size="sm"
            onClick={simulateConceptDetection}
            variant="outline"
          >
            Concept
          </Button>
          <Button
            size="sm"
            onClick={simulateSkillUpdate}
            variant="outline"
          >
            Skill Update
          </Button>
          <Button
            size="sm"
            onClick={simulatePromptGeneration}
            variant="outline"
          >
            Prompt
          </Button>
        </HStack>
      </VStack>
    </VStack>
  );
};

export default DebugControls;
